import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Drawer,
  IconButton,
  LinearProgress,
  Chip,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { toast } from "react-toastify";
import { commissionsApi } from "../../Components/commissionsApi";
import { useTheme } from "../../hooks/useTheme";
import { createMuiThemeFromMode } from "../../theme/theme";
import styles from "../AllEnquiries.module.scss";

const baseThemeOptions = {
  palette: { secondary: { main: "#f5365c" }, success: { main: "#2dce89" }, warning: { main: "#fb6340" }, info: { main: "#11cdef" } },
  typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", button: { textTransform: "none", fontWeight: 500 } },
  shape: { borderRadius: 12 },
};

const SOURCE_LABELS = {
  total_business_revenue: "Revenue",
  total_conversions: "Conversions",
  conversions_by_program: "Conversions (Program)",
  conversions_by_course: "Conversions (Course)",
};

function MyCommissions() {
  const navigate = useNavigate();
  const { themeMode } = useTheme();
  const theme = useMemo(() => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)), [themeMode]);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bracketSnackbar, setBracketSnackbar] = useState(null);

  useEffect(() => {
    commissionsApi
      .my()
      .then((res) => setStructures(res.structures || []))
      .catch(() => setStructures([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    for (const s of structures) {
      const progress = s.progress;
      if (!progress?.rules) continue;
      for (const r of progress.rules) {
        const bracket = r.current_bracket;
        if (!bracket) continue;
        const maxVal = bracket.max_value;
        if (maxVal != null && maxVal > 0) {
          const metric = Number(r.metric_value);
          const gap = maxVal - metric;
          if (gap > 0 && gap <= 2) {
            setBracketSnackbar({
              message: `You are ${gap === 1 ? "1" : gap} ${r.source === "total_conversions" ? "conversion" : "unit"} away from unlocking the next commission tier for ${s.name}.`,
            });
            break;
          }
        }
      }
    }
  }, [structures]);

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        <Paper elevation={0} className={styles.contentPaper} sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h4" className={styles.pageTitle}>My Commissions</Typography>
                <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                  View your active commission structures and progress.
                </Typography>
              </Box>

              {loading && (
                <Grid container spacing={2}>
                  {[1, 2, 3].map((i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                      <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
                    </Grid>
                  ))}
                </Grid>
              )}

              {!loading && structures.length === 0 && (
                <Card sx={{ p: 4, textAlign: "center" }}>
                  <AccountBalanceIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">No commission structures assigned</Typography>
                  <Typography variant="body2" color="text.secondary">You don't have any active commission structures. Contact your manager.</Typography>
                </Card>
              )}

              {!loading && structures.length > 0 && (
                <Grid container spacing={2}>
                  {structures.map((s) => {
                    const progress = s.progress || {};
                    const rules = progress.rules || [];
                    const totalEarned = Number(s.commission_earned || 0);
                    const primaryRule = rules[0];
                    const metricLabel = primaryRule ? SOURCE_LABELS[primaryRule.source] || primaryRule.source : "Progress";
                    const metricValue = primaryRule ? Number(primaryRule.metric_value) : 0;
                    const bracket = primaryRule?.current_bracket;
                    const maxBracket = bracket?.max_value != null ? Number(bracket.max_value) : null;
                    const progressPercent = maxBracket && maxBracket > 0 ? Math.min(100, (metricValue / maxBracket) * 100) : 0;

                    return (
                      <Grid item xs={12} sm={6} md={4} key={s.id}>
                        <Card sx={{ height: "100%", borderRadius: 2 }}>
                          <CardContent>
                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
                              <Typography variant="h6" fontWeight={600}>{s.name}</Typography>
                              <Chip label={s.timeframe ? String(s.timeframe).charAt(0).toUpperCase() + s.timeframe.slice(1) : ""} size="small" variant="outlined" />
                            </Box>
                            <Typography variant="body2" color="text.secondary">{metricLabel}</Typography>
                            <Typography variant="h5" sx={{ my: 1 }}>{metricValue}</Typography>
                            {maxBracket != null && maxBracket > 0 && (
                              <>
                                <LinearProgress variant="determinate" value={progressPercent} sx={{ height: 8, borderRadius: 1, mb: 1 }} />
                                <Typography variant="caption" color="text.secondary">Progress toward next bracket: {metricValue} / {maxBracket}</Typography>
                              </>
                            )}
                            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                              <Typography variant="body2" color="text.secondary">Commission Earned</Typography>
                              <Typography variant="h6" color="success.main">${totalEarned.toFixed(2)}</Typography>
                            </Box>
                            <Button size="small" fullWidth sx={{ mt: 2 }} onClick={() => navigate(`/commissions/${s.id}`)}>View details</Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Paper>

        <Snackbar open={!!bracketSnackbar} autoHideDuration={6000} onClose={() => setBracketSnackbar(null)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
          <Alert onClose={() => setBracketSnackbar(null)} severity="info" variant="filled">
            Great job! {bracketSnackbar?.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}

export default MyCommissions;
